import type { ScheduleType } from './schedule-types';

/** Optional row fields when `eventType` alone is ambiguous (e.g. violations-closed classes). */
export type ScheduleTypeContext = {
	programId?: string;
	legacyLessonId?: string;
};

export type ScheduleTypeHandler = {
	readonly type: ScheduleType;
	/** FullCalendar event background color. */
	readonly calendarColor: string;
	/** FullCalendar event title/text color. */
	readonly calendarTextColor: string;
	readonly matchesStoreEventType: (eventType: string) => boolean;
};

/** Legacy `eventType` on `mas-schedules` docs maps into canonical schedule types. */
export const SCHEDULE_TYPE_HANDLERS: readonly ScheduleTypeHandler[] = [
	{
		type: 'class',
		calendarColor: '#ffc107',
		calendarTextColor: '#000000',
		matchesStoreEventType: eventType => eventType === 'default' || eventType === 'class',
	},
	{
		type: 'test',
		calendarColor: '#3b82f6',
		calendarTextColor: '#ffffff',
		matchesStoreEventType: eventType => eventType === 'test' || eventType === 'testing',
	},
	{
		type: 'event',
		calendarColor: '#22c55e',
		calendarTextColor: '#000000',
		matchesStoreEventType: eventType => eventType === 'event' || eventType === 'closed',
	},
	{
		type: 'lesson',
		calendarColor: '#a855f7',
		calendarTextColor: '#ffffff',
		matchesStoreEventType: eventType => eventType === 'lesson',
	},
] as const;

export function resolveScheduleType(eventType: string, context?: ScheduleTypeContext): ScheduleType {
	const normalized = String(eventType ?? '').trim().toLowerCase();
	// Past classes are closed by the violations job (`eventType: closed`) but keep `programId`.
	if (normalized === 'closed' && context?.programId?.trim() && !context.legacyLessonId?.trim()) {
		return 'class';
	}
	for (const handler of SCHEDULE_TYPE_HANDLERS) {
		if (handler.matchesStoreEventType(normalized)) {
			return handler.type;
		}
	}
	return 'class';
}

export function getScheduleTypeHandler(type: ScheduleType): ScheduleTypeHandler {
	const handler = SCHEDULE_TYPE_HANDLERS.find(h => h.type === type);
	return handler ?? SCHEDULE_TYPE_HANDLERS[0];
}

export function getScheduleTypeHandlerForStoreEventType(
	eventType: string,
	context?: ScheduleTypeContext,
): ScheduleTypeHandler {
	return getScheduleTypeHandler(resolveScheduleType(eventType, context));
}

const SCHEDULE_TYPE_LABELS: Record<ScheduleType, string> = {
	class: 'Class',
	test: 'Test',
	event: 'Event',
	lesson: 'Lesson',
};

export function scheduleTypeDisplayLabel(type: ScheduleType): string {
	return SCHEDULE_TYPE_LABELS[type];
}

/** Legend rows for calendar UI (colors match FullCalendar events). */
export function listScheduleTypeLegend(): ReadonlyArray<{
	readonly type: ScheduleType;
	readonly label: string;
	readonly calendarColor: string;
	readonly calendarTextColor: string;
}> {
	return SCHEDULE_TYPE_HANDLERS.map(handler => ({
		type: handler.type,
		label: scheduleTypeDisplayLabel(handler.type),
		calendarColor: handler.calendarColor,
		calendarTextColor: handler.calendarTextColor,
	}));
}
